import React from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BaseStep, FormField, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import { Toggle } from '../Toggle';
import { RadioButton } from '../RadioButton';
import { SDGSelector } from '../SDGSelector';
import CountryRegionSelector from '../CountryRegionSelector';
import companyIcon from '../../../../assets/icons/company2.png';
import processingIcon from '../../../../assets/icons/processing.png';
import profileGrey from '../../../../assets/profile_icon__grey-correct_.png';
import { countriesToSave, countryRegions, usStates } from '../../../../constants/countries';
import CountrySelect from '../../../CountrySelect';
import { SDG_IMAGES } from '../../../../constants/sdgImages';
import { useAuth } from '../../../../contexts/AuthContext';
import { useProfile } from '../../../../contexts/ProfileContext';
import { loadPricingSignupDraft } from '../../../../utils/pricingSignupDraft';
import {
  countryRequiresStateSubdivision,
  isUnitedKingdomCountry,
  isUnitedStatesCountry,
} from '../../../../utils/investorPricingView';
import { UK_COUNTIES } from '../../../../constants/ukLocations';
import CitySelect, { NATIVE_SELECT_CHEVRON_STYLE } from '../CitySelect';

function normalizeSelectedSdgs(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(v => (typeof v === 'number' ? v : Number(v)))
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 17);
}

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB
const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const CompanyStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { user, refreshUser } = useAuth();
  const { investorProfile } = useProfile();

  // Helper function to convert investAs array to boolean states
  const getInvestAsArray = (): string[] => {
    // For now, handle both single value and array formats
    if (!investorProfile?.investAs) return [];
    if (Array.isArray(investorProfile.investAs)) {
      return investorProfile.investAs;
    }
    // If it's a single value, convert to array
    return [investorProfile.investAs];
  };

  const { formData, errors, handleInputChange, validateForm, setFormData } = useFormData({
    // Basic Information
    name: user?.name || '',
    companyName: investorProfile?.companyName || '',
    email: user?.email || investorProfile?.email || loadPricingSignupDraft('investor')?.email || '',
    country: investorProfile?.country || user?.country || '',
    state: investorProfile?.state || '',
    city: investorProfile?.city || '',
    previousInvestments: investorProfile?.previousInvestments || '',
    investmentGroups:
      (investorProfile as { investmentGroups?: string } | null)?.investmentGroups || '',
  });

  // State for investment type toggles (array of selected values)
  const investAsArray = getInvestAsArray();
  const [equityInvestment, setEquityInvestment] = React.useState<boolean>(
    investAsArray.includes('equity')
  );
  const [revenueBased, setRevenueBased] = React.useState<boolean>(
    investAsArray.includes('revenue-based')
  );
  const [servicesEquity, setServicesEquity] = React.useState<boolean>(
    investAsArray.includes('services-equity')
  );

  // Helper function to detect which regions are fully represented by saved countries
  const detectRegionsFromCountries = (countries: string[]): string[] => {
    if (!countries || countries.length === 0) return [];

    const detectedRegions: string[] = [];
    const countrySet = new Set(countries);

    // Check each region
    Object.keys(countryRegions).forEach(regionKey => {
      const regionCountries = countryRegions[regionKey as keyof typeof countryRegions] || [];

      // Check if ALL countries from this region are in the saved countries
      const allCountriesPresent = regionCountries.every(country => countrySet.has(country));

      // Only add region if it has countries and all are present
      if (regionCountries.length > 0 && allCountriesPresent) {
        detectedRegions.push(regionKey);
      }
    });

    return detectedRegions;
  };

  // State for countries and regions selection
  const [selectedCountries, setSelectedCountries] = React.useState<string[]>(() => {
    // Check if countries contains 'ANYWHERE'
    if (
      investorProfile?.countries &&
      investorProfile.countries.length === 1 &&
      investorProfile.countries[0] === 'ANYWHERE'
    ) {
      return [];
    }
    return investorProfile?.countries || [];
  });
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(() => {
    // Detect regions from saved countries
    if (
      investorProfile?.countries &&
      investorProfile.countries.length > 0 &&
      investorProfile.countries[0] !== 'ANYWHERE'
    ) {
      return detectRegionsFromCountries(investorProfile.countries);
    }
    return [];
  });
  const [isAnywhereSelected, setIsAnywhereSelected] = React.useState<boolean>(() => {
    // Check if countries contains 'ANYWHERE' or if there are no countries
    if (
      investorProfile?.countries &&
      investorProfile.countries.length === 1 &&
      investorProfile.countries[0] === 'ANYWHERE'
    ) {
      return true;
    }
    return !investorProfile?.countries || investorProfile.countries.length === 0;
  });

  // State for SDG preference — hydrate from saved profile in edit mode
  const savedSdgs = React.useMemo(
    () => normalizeSelectedSdgs(investorProfile?.selectedSDGs),
    [investorProfile?.selectedSDGs]
  );
  const [hasSDGPreference, setHasSDGPreference] = React.useState<boolean | null>(() => {
    if (savedSdgs.length > 0) return true;
    // Profile already exists with empty SDGs → treat as explicit "No"
    if (investorProfile) return false;
    return null;
  });
  const [selectedSDGs, setSelectedSDGs] = React.useState<number[]>(() => savedSdgs);
  const [isEditingSdgs, setIsEditingSdgs] = React.useState<boolean>(() => savedSdgs.length === 0);
  const hydratedSdgsRef = React.useRef(false);

  // Re-hydrate once profile context finishes loading (edit mode)
  React.useEffect(() => {
    if (hydratedSdgsRef.current || !investorProfile) return;
    hydratedSdgsRef.current = true;
    const next = normalizeSelectedSdgs(investorProfile.selectedSDGs);
    setSelectedSDGs(next);
    setHasSDGPreference(next.length > 0 ? true : false);
    setIsEditingSdgs(next.length === 0);
  }, [investorProfile]);

  // Handle SDG selection
  const handleSDGSelect = (sdg: number) => {
    setSelectedSDGs(prev => {
      if (prev.includes(sdg)) {
        return prev.filter(s => s !== sdg);
      }
      return [...prev, sdg];
    });
  };

  // Profile can arrive after the first render. Don't put those saved answers back over edits.
  const investmentsTouchedRef = React.useRef(false);

  React.useEffect(() => {
    if (investmentsTouchedRef.current || !investorProfile) return;
    const savedGroups = (investorProfile as { investmentGroups?: string }).investmentGroups || '';
    const savedTypes = getInvestAsArray();
    setEquityInvestment(savedTypes.includes('equity'));
    setRevenueBased(savedTypes.includes('revenue-based'));
    setServicesEquity(savedTypes.includes('services-equity'));
    setFormData(prev => ({
      ...prev,
      previousInvestments:
        typeof investorProfile.previousInvestments === 'string'
          ? investorProfile.previousInvestments
          : prev.previousInvestments,
      investmentGroups: savedGroups,
    }));
  }, [investorProfile, setFormData]);

  // Update formData when investment type toggles change
  React.useEffect(() => {
    // Build array of selected investment types
    const selectedTypes: ('equity' | 'revenue-based' | 'services-equity')[] = [];
    if (equityInvestment) {
      selectedTypes.push('equity');
    }
    if (revenueBased) {
      selectedTypes.push('revenue-based');
    }
    if (servicesEquity) {
      selectedTypes.push('services-equity');
    }

    setFormData(prev => ({
      ...prev,
      investAs: selectedTypes,
    }));
  }, [equityInvestment, revenueBased, servicesEquity, setFormData]);

  // Update formData when countries/regions change
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      // An explicit country is what gets saved. Worldwide only when nothing else is picked.
      countries: countriesToSave(selectedCountries, isAnywhereSelected),
    }));
  }, [selectedCountries, isAnywhereSelected, setFormData]);

  // Persist SDG preference into formData so Save actually writes selectedSDGs
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      selectedSDGs: hasSDGPreference === true ? selectedSDGs : [],
    }));
  }, [hasSDGPreference, selectedSDGs, setFormData]);

  // True after the investor picks a headquarters country in this visit.
  // Save calls refreshUser(), which replaces `user`. Without this, that refresh
  // writes the account country back over the dropdown and the old country is saved.
  const countryTouchedRef = React.useRef(false);
  // Same idea for name/email — don't put the account value back after the user clears the field.
  const nameTouchedRef = React.useRef(false);
  const emailTouchedRef = React.useRef(false);

  React.useEffect(() => {
    if (countryTouchedRef.current) return;
    const savedCountry =
      (typeof investorProfile?.country === 'string' && investorProfile.country.trim()) ||
      (typeof user?.country === 'string' && user.country.trim()) ||
      '';
    if (!savedCountry) return;
    setFormData(prev =>
      prev.country === savedCountry ? prev : { ...prev, country: savedCountry }
    );
  }, [user, investorProfile, setFormData]);

  // Seed name and email once from the account / pricing-page draft (late-loading user).
  React.useEffect(() => {
    if (!nameTouchedRef.current && user?.name) {
      setFormData(prev => (prev.name ? prev : { ...prev, name: user.name }));
    }
    if (!emailTouchedRef.current) {
      const nextEmail =
        user?.email || investorProfile?.email || loadPricingSignupDraft('investor')?.email || '';
      if (nextEmail) {
        setFormData(prev => (prev.email ? prev : { ...prev, email: nextEmail }));
      }
    }
  }, [user, investorProfile?.email, setFormData]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    nameTouchedRef.current = true;
    handleInputChange(e);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emailTouchedRef.current = true;
    handleInputChange(e);
  };

  // Profile picture state. A later profile load must not put the old photo back.
  const pictureTouchedRef = React.useRef(false);
  const [pictureUrl, setPictureUrl] = React.useState<string>(
    investorProfile?.profilePictureUrl || ''
  );

  React.useEffect(() => {
    if (pictureTouchedRef.current) return;
    const saved = investorProfile?.profilePictureUrl?.trim() || '';
    if (!saved) return;
    setPictureUrl(saved);
  }, [investorProfile?.profilePictureUrl]);
  const [pictureUploading, setPictureUploading] = React.useState(false);
  const [pictureError, setPictureError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPictureError(null);

    if (!VALID_TYPES.includes(file.type)) {
      setPictureError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setPictureError('Image must be smaller than 1 MB.');
      return;
    }

    setPictureUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const functions = getFunctions();
      const upload = httpsCallable<
        { userId: string; file: { name: string; contentType: string; data: string } },
        { success: boolean; url?: string; error?: string }
      >(functions, 'uploadInvestorPicture');

      const result = await upload({
        userId: user!.id,
        file: { name: file.name, contentType: file.type, data: base64 },
      });

      if (result.data.success && result.data.url) {
        const storedUrl = result.data.url;
        const freshUrl = storedUrl.includes('v=')
          ? storedUrl
          : `${storedUrl}${storedUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
        pictureTouchedRef.current = true;
        setPictureUrl(freshUrl);
        setFormData(prev => ({ ...prev, profilePictureUrl: freshUrl }));
      } else {
        setPictureError(result.data.error || 'Upload failed. Please try again.');
      }
    } catch {
      setPictureError('Upload failed. Please try again.');
    } finally {
      setPictureUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Track if we need to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);

  // Latest form values for the save check. Refreshing the account must not
  // restart this effect, or Save never hears back and the button does nothing.
  const saveCheckRef = React.useRef({
    formData,
    validateForm,
    equityInvestment,
    revenueBased,
    servicesEquity,
    hasSDGPreference,
    selectedSDGs,
    user,
    refreshUser,
    onValidationResult,
    onValidationHandled,
  });
  saveCheckRef.current = {
    formData,
    validateForm,
    equityInvestment,
    revenueBased,
    servicesEquity,
    hasSDGPreference,
    selectedSDGs,
    user,
    refreshUser,
    onValidationResult,
    onValidationHandled,
  };

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (!shouldValidate) return;

    let cancelled = false;

    const runValidation = async () => {
      const current = saveCheckRef.current;
      // Snapshot before refreshUser(). That refresh must not replace the country
      // the investor just chose.
      const selectedTypes: ('equity' | 'revenue-based' | 'services-equity')[] = [];
      if (current.equityInvestment) selectedTypes.push('equity');
      if (current.revenueBased) selectedTypes.push('revenue-based');
      if (current.servicesEquity) selectedTypes.push('services-equity');
      const formAtSave = {
        ...current.formData,
        previousInvestments: String(current.formData.previousInvestments || '').trim(),
        investmentGroups: String(current.formData.investmentGroups || ''),
        investAs: selectedTypes,
      } as typeof current.formData & {
        previousInvestments: string;
        investmentGroups: string;
        investAs: ('equity' | 'revenue-based' | 'services-equity')[];
      };
      const requiredFields = [
        'name',
        'email',
        'country',
        'city',
        'previousInvestments',
        ...(countryRequiresStateSubdivision(String(formAtSave.country || ''))
          ? (['state'] as const)
          : []),
      ];
      const isValid = current.validateForm([...requiredFields]);
      const emailValue = String(formAtSave.email || '')
        .trim()
        .toLowerCase();
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

      const sdgPreferenceAnswered =
        current.hasSDGPreference === true || current.hasSDGPreference === false;
      const sdgsValid =
        current.hasSDGPreference !== true ||
        (Array.isArray(current.selectedSDGs) && current.selectedSDGs.length > 0);
      let finalIsValid = isValid && emailValid && sdgPreferenceAnswered && sdgsValid;

      const countryValue = String(formAtSave.country || '').trim();
      if (finalIsValid && current.user) {
        try {
          const updateUserFn = httpsCallable(getFunctions(), 'updateUser');
          const result = await updateUserFn({
            userId: current.user.id,
            updates: {
              name: String(formAtSave.name || '').trim(),
              email: emailValue,
              ...(countryValue ? { country: countryValue } : {}),
            },
          });
          const data = result.data as { success?: boolean; error?: string };
          if (!data?.success) {
            finalIsValid = false;
          } else {
            await current.refreshUser();
          }
        } catch {
          finalIsValid = false;
        }
      }

      if (cancelled) return;

      setShowErrors(true);

      if (current.onValidationResult) {
        current.onValidationResult(finalIsValid, formAtSave);
      }
      if (current.onValidationHandled) {
        current.onValidationHandled();
      }
    };

    void runValidation();
    return () => {
      cancelled = true;
    };
    // Only re-run when Save is pressed. Other deps are read from saveCheckRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldValidate]);

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      {/* Company Section */}
      <FormSection icon={companyIcon} title="COMPANY">
        <FormField
          name="name"
          label="Your Name *"
          required
          value={String(formData.name || '')}
          onChange={handleNameChange}
          error={showErrors ? errors.name : undefined}
          placeholder="Enter your name"
        />

        <FormField
          name="companyName"
          label="Company Name (if no company, leave it blank)"
          value={String(formData.companyName || '')}
          onChange={handleInputChange}
          error={showErrors ? errors.companyName : undefined}
          placeholder="Enter company name"
        />

        <FormField
          name="email"
          label="Email *"
          type="email"
          required
          value={String(formData.email || '')}
          onChange={handleEmailChange}
          error={showErrors ? errors.email : undefined}
          placeholder="Enter your email"
        />

        <div className="mb-4 text-left">
          <span className="block text-sm sm:text-base font-medium text-white mb-2">
            Profile photo <span className="text-gray-400 font-normal">(optional)</span>
          </span>
          <div className="flex items-start gap-4">
            <div className="relative flex-shrink-0">
              <img
                key={pictureUrl || 'placeholder'}
                src={pictureUrl || profileGrey}
                alt="Profile picture"
                className={`w-16 h-16 rounded-full ${
                  pictureUrl
                    ? 'object-cover border border-[#444444]'
                    : 'object-contain overflow-hidden border border-[#444444] bg-dark-950 px-[8%] pt-[16%] pb-0'
                }`}
              />
              {pictureUploading && (
                <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                  <span className="inline-block w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1 min-w-0 pt-0.5">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
                onChange={handlePictureChange}
                disabled={pictureUploading}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={pictureUploading}
                className="text-sm text-emerald-400 hover:text-emerald-300 underline text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pictureUrl ? 'Change photo' : 'Upload profile photo'}
              </button>
              <p className="text-xs text-gray-500">JPEG, PNG or WebP · max 1 MB</p>
              {pictureError && <p className="text-xs text-red-400">{pictureError}</p>}
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="country"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Location of Investor / Company (country where you are officially headquartered) *
          </label>
          <CountrySelect
            id="country"
            name="country"
            value={String(formData.country || '')}
            onChange={nextCountry => {
              countryTouchedRef.current = true;
              const prevCountry = String(formData.country || '');
              handleInputChange({
                target: { name: 'country', value: nextCountry },
              } as React.ChangeEvent<HTMLInputElement>);
              if (
                countryRequiresStateSubdivision(prevCountry) !==
                  countryRequiresStateSubdivision(nextCountry) ||
                isUnitedStatesCountry(prevCountry) !== isUnitedStatesCountry(nextCountry) ||
                isUnitedKingdomCountry(prevCountry) !== isUnitedKingdomCountry(nextCountry)
              ) {
                handleInputChange({
                  target: { name: 'state', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            required
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url('data:image/svg+xml,%3csvg xmlns=\\'http://www.w3.org/2000/svg\\' fill=\\'none\\' viewBox=\\'0 0 20 20\\'%3e%3cpath stroke=\\'%236b7280\\' stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'1.5\\' d=\\'m6 8 4 4 4-4\\'/%3e%3c/svg%3e')] pr-10 ${
              showErrors && errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px 16px',
              paddingRight: '40px',
            }}
          />
          {showErrors && errors.country && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.country}</p>
          )}
        </div>

        {isUnitedStatesCountry(String(formData.country || '')) && (
          <div>
            <label
              htmlFor="state"
              className={`block text-sm sm:text-base font-medium mb-2 text-left ${
                showErrors && errors.state ? 'text-red-500' : 'text-white'
              }`}
            >
              State *
            </label>
            <select
              id="state"
              name="state"
              value={String(formData.state || '')}
              onChange={e => {
                handleInputChange(e);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              required
              className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none ${
                showErrors && errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
              style={NATIVE_SELECT_CHEVRON_STYLE}
            >
              <option value="">Select state</option>
              {(String(formData.state || '').trim() &&
              !usStates.includes(String(formData.state || '').trim())
                ? [String(formData.state || '').trim(), ...usStates]
                : usStates
              ).map(state => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {showErrors && errors.state ? (
              <p className="text-red-500 text-sm mt-1 text-left">Please select your state.</p>
            ) : null}
          </div>
        )}

        {isUnitedKingdomCountry(String(formData.country || '')) && (
          <div>
            <label
              htmlFor="state"
              className={`block text-sm sm:text-base font-medium mb-2 text-left ${
                showErrors && errors.state ? 'text-red-500' : 'text-white'
              }`}
            >
              County *
            </label>
            <select
              id="state"
              name="state"
              value={String(formData.state || '')}
              onChange={e => {
                handleInputChange(e);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              required
              className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none ${
                showErrors && errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
              style={NATIVE_SELECT_CHEVRON_STYLE}
            >
              <option value="">Select county</option>
              {(String(formData.state || '').trim() &&
              !(UK_COUNTIES as readonly string[]).includes(String(formData.state || '').trim())
                ? [String(formData.state || '').trim(), ...UK_COUNTIES]
                : UK_COUNTIES
              ).map(county => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
            {showErrors && errors.state ? (
              <p className="text-red-500 text-sm mt-1 text-left">Please select your county.</p>
            ) : null}
          </div>
        )}

        {String(formData.country || '').trim() ? (
          <div>
            <label
              htmlFor="city"
              className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
            >
              City *
            </label>
            <CitySelect
              id="city"
              name="city"
              country={String(formData.country || '')}
              region={String(formData.state || '')}
              value={String(formData.city || '')}
              required
              onChange={city =>
                handleInputChange({
                  target: { name: 'city', value: city },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
                showErrors && errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              error={Boolean(showErrors && errors.city)}
            />
            {showErrors && errors.city && (
              <p className="text-red-500 text-sm mt-1 text-right">{errors.city}</p>
            )}
          </div>
        ) : null}

        <FormField
          name="previousInvestments"
          label="Previous startup investments (by you or your firm). If none, type n/a *"
          required
          value={String(formData.previousInvestments || '')}
          onChange={event => {
            investmentsTouchedRef.current = true;
            handleInputChange(event);
          }}
          error={showErrors ? errors.previousInvestments : undefined}
          placeholder="Describe your previous investments..."
        />

        <FormField
          name="investmentGroups"
          label="If you're a member of any investment groups, please mention them here"
          value={String(formData.investmentGroups || '')}
          onChange={event => {
            investmentsTouchedRef.current = true;
            handleInputChange(event);
          }}
          placeholder="Mention any investment groups you're part of..."
        />

        <div className="mt-6">
          <label className="text-sm sm:text-base font-medium text-white mb-4 block text-left">
            The majority of your investments are likely to be in:
          </label>
          <div className="space-y-4">
            <Toggle
              name="equityInvestment"
              label="Equity investments (cash for ownership)"
              value={equityInvestment}
              onChange={value => {
                investmentsTouchedRef.current = true;
                setEquityInvestment(value);
              }}
            />
            <Toggle
              name="revenueBased"
              label="Revenue-based financing (lending based on startup's revenue)"
              value={revenueBased}
              onChange={value => {
                investmentsTouchedRef.current = true;
                setRevenueBased(value);
              }}
            />
            <Toggle
              name="servicesEquity"
              label="Services in exchange for equity (little or no cash provided)"
              value={servicesEquity}
              onChange={value => {
                investmentsTouchedRef.current = true;
                setServicesEquity(value);
              }}
            />
          </div>
        </div>
      </FormSection>

      {/* Match Processing Section */}
      <FormSection icon={processingIcon} title="MATCH PROCESSING">
        <CountryRegionSelector
          selectedCountries={selectedCountries}
          selectedRegions={selectedRegions}
          isAnywhereSelected={isAnywhereSelected}
          onCountriesChange={setSelectedCountries}
          onRegionsChange={setSelectedRegions}
          onAnywhereChange={setIsAnywhereSelected}
        />

        <div className="mt-8">
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-4 text-left">
            Preferences toward startups working on any specific SDGs?
          </h3>
          <RadioButton
            name="sdgPreference"
            value={hasSDGPreference}
            onChange={value => {
              setHasSDGPreference(value);
              // If changing to false, clear selected SDGs
              if (!value) {
                setSelectedSDGs([]);
                setIsEditingSdgs(false);
              } else {
                setIsEditingSdgs(selectedSDGs.length === 0);
              }
            }}
          />
          {showErrors && hasSDGPreference === null && (
            <p className="text-red-500 text-sm mt-2 text-right">Please answer the SDG preference</p>
          )}

          {hasSDGPreference === true && (
            <div className="mt-6">
              {selectedSDGs.length > 0 && !isEditingSdgs ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm sm:text-base font-medium text-white text-left">
                      Selected SDGs
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsEditingSdgs(true)}
                      className="text-sm text-emerald-400 hover:text-emerald-300 underline"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 sm:gap-3">
                    {selectedSDGs
                      .slice()
                      .sort((a, b) => a - b)
                      .map(sdg => (
                        <div key={sdg} className="w-full">
                          <img
                            src={SDG_IMAGES[sdg]}
                            alt={`SDG Goal ${sdg}`}
                            className="w-full h-auto rounded-lg"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <label className="block text-sm sm:text-base font-medium text-white text-left">
                      Select one or more from the following:
                    </label>
                    {selectedSDGs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setIsEditingSdgs(false)}
                        className="text-sm text-emerald-400 hover:text-emerald-300 underline"
                      >
                        Done
                      </button>
                    )}
                  </div>
                  <SDGSelector selectedSDGs={selectedSDGs} onSDGSelect={handleSDGSelect} />
                  {showErrors && selectedSDGs.length === 0 && (
                    <p className="text-red-500 text-sm mt-2 text-right">
                      Select at least one SDG, or choose No above
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default CompanyStep;
